/**
 * ClosedCardRowContainer — adapts a closed kanban card onto the shared Player
 * Card v7 renderer and owns the row's overflow-menu state (SE-6862).
 *
 * The adapter is the substance here. `MobileTargetCardView` is the registry's
 * component for `Player Card v7`, but it was built for the scouting list, whose
 * query supplies more per-player context than the board's does. The fields the
 * board cannot answer are passed as null RATHER than approximated: v7 hides a
 * null row entirely, so the closed card renders as a smaller true card instead
 * of a full card carrying invented values. That degradation is the accepted
 * shape for this surface (ruling P2 on the ticket), not an oversight.
 * @module features/player-kanban/components/organisms/ClosedArea/ClosedCardRow/ClosedCardRowContainer
 */

import { Undo2 } from "lucide-react-native";
import { useCallback, useMemo, useState } from "react";

import { derivePlayerInitials } from "@/utils/playerInitials";

import ClosedCardRowView, {
  type ClosedPlayerCardProps,
} from "./ClosedCardRowView";
import { formatCardUpdatedAgo } from "../../../../utils/cardUpdatedAgo";

import type { ClosedCardEntry } from "../../../../types/types";
import type { DesktopDropdownMenuItem } from "@/components/molecules/DesktopDropdownMenu";

/**
 * Inert handlers for the v7 affordances the Closed area does not offer.
 *
 * Module-level so the card receives the SAME reference on every render
 * (`react-perf/jsx-no-new-function-as-prop`). A closed card is history: opening
 * the positions sheet or the manage-watchlists sheet from it would invite an
 * edit to a card that is no longer in play, so both are deliberately dead
 * rather than wired.
 */
const NO_OP = (): void => undefined;

/** Props for {@link ClosedCardRowContainer}. */
export interface ClosedCardRowContainerProps {
  /** The closed card to render. */
  readonly entry: ClosedCardEntry;
  /** Whether the card renders de-emphasised (a non-signed outcome). */
  readonly dimmed: boolean;
  /** Whether the viewer may reopen cards. */
  readonly canReopen: boolean;
  /** Reopen-request handler, given the entry it was raised from. */
  readonly onReopenPress: (entry: ClosedCardEntry) => void;
}

/**
 * Container for one closed card row.
 * @param props - {@link ClosedCardRowContainerProps}
 * @param props.entry - The closed card to render
 * @param props.dimmed - Whether to de-emphasise the card
 * @param props.canReopen - Whether the viewer may reopen
 * @param props.onReopenPress - Reopen-request handler
 * @returns The closed card row.
 */
const ClosedCardRowContainer = ({
  entry,
  dimmed,
  canReopen,
  onReopenPress,
}: ClosedCardRowContainerProps) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { player } = entry;

  const onMenuToggle = useCallback(() => setIsMenuOpen(prev => !prev), []);
  const onMenuClose = useCallback(() => setIsMenuOpen(false), []);

  const onReopen = useCallback(
    () => onReopenPress(entry),
    [onReopenPress, entry]
  );

  const cardProps = useMemo<ClosedPlayerCardProps>(
    () => ({
      playerName: player.name,
      initials: derivePlayerInitials(player.name),
      clubName: player.teamName,
      age: player.age ?? null,
      monthsOnContract: player.contractMonths,
      gpr: player.gpr ?? null,
      position: player.position ?? "",
      // The board carries one position per card, so there is never an overflow
      // count to show — and the sheet that would reveal them is inert here.
      positionExtraCount: 0,
      updatedLabel: formatCardUpdatedAgo(player.updatedAt),
      // Everything below is absent from the board's card query. v7 hides each
      // of these when null, which is exactly what this surface wants.
      scoreBadgeLabel: null,
      avgScoutRating: null,
      scoutReportCount: null,
      squadStatus: null,
      pipelineTier: null,
      scoutName: null,
      watchlists: [],
      onPositionsPress: NO_OP,
      onListPress: NO_OP,
      // Tapping the card raises the same reopen request the inline link does:
      // reopening is the only action a closed card supports, so the whole card
      // is the affordance rather than a dead surface with a live corner.
      //
      // That affordance is GATED on permission. The visible controls already
      // disappear for a viewer who cannot reopen, but the card body is not a
      // control — it stays pressable either way — so leaving the handler bound
      // let such a viewer open the reopen dialog by pressing anywhere on the
      // card. Hiding the buttons is not the permission check; this is.
      onPress: canReopen ? onReopen : NO_OP,
    }),
    [player, onReopen, canReopen]
  );

  const menuItems = useMemo<DesktopDropdownMenuItem[]>(
    () => [
      {
        id: "reopen",
        label: "Reopen…",
        icon: Undo2,
        onPress: onReopen,
      },
    ],
    [onReopen]
  );

  return (
    <ClosedCardRowView
      cardProps={cardProps}
      playerName={player.name}
      dimmed={dimmed}
      canReopen={canReopen}
      onReopen={onReopen}
      isMenuOpen={isMenuOpen}
      onMenuToggle={onMenuToggle}
      onMenuClose={onMenuClose}
      menuItems={menuItems}
    />
  );
};

ClosedCardRowContainer.displayName = "ClosedCardRowContainer";

export default ClosedCardRowContainer;
